import '@/lib/errorReporter';
import { enableMapSet } from 'immer';
enableMapSet();
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRootRoute,
  createRoute,
  createRouter,
  RouterProvider,
  Outlet,
} from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Home, Plus, User } from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { MobileShell, type MobileTab } from '@/components/mobile/MobileShell';
import { SplashScreen } from '@/components/mobile/SplashScreen';
import { applyBrandCssVars } from '@/brand/brand';
import '@/index.css';
import '@/styles/transitions.css';
import { HomeScreen } from '@/screens/HomeScreen';
import { AddScreen } from '@/screens/AddScreen';
import { ProfileScreen } from '@/screens/ProfileScreen';

const queryClient = new QueryClient();

// Publish the brand as CSS variables BEFORE the first paint, so nothing renders in default colours
// and then snaps into the brand. Re-applied when the OS colour scheme flips.
applyBrandCssVars();
window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', applyBrandCssVars);

/**
 * The bottom tab bar IS the app's navigation. Rename, reorder or change these tabs to fit the app,
 * but keep the shape: 3-5 tabs, each a real route. Do not replace it with a hamburger menu or with
 * anchor links on one long page — that is what gets an app rejected as a repackaged website.
 */
const TABS: MobileTab[] = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/add', label: 'Add', icon: Plus },
  { to: '/profile', label: 'You', icon: User },
];

const rootRoute = createRootRoute({
  component: () => (
    <MobileShell tabs={TABS}>
      <Outlet />
    </MobileShell>
  ),
  errorComponent: RouteErrorBoundary,
});

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomeScreen });
const addRoute = createRoute({ getParentRoute: () => rootRoute, path: '/add', component: AddScreen });
const profileRoute = createRoute({ getParentRoute: () => rootRoute, path: '/profile', component: ProfileScreen });

const routeTree = rootRoute.addChildren([indexRoute, addRoute, profileRoute]);

const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Offline app shell. Registered in production only so the dev server's HMR is never served from cache.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* SW unsupported or blocked — the app still works, just without offline web support */
    });
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        {/* The splash sits above the app and removes itself; the app mounts underneath it
            immediately, so nothing is waiting on the animation to finish. */}
        <SplashScreen />
        <RouterProvider router={router} />
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
);
